<script lang="ts" setup>
	import { useHotelsStore } from '@/stores/hotels'
	import moment from 'moment'

	import LoadingSpin from '@/components/icons/LoadingSpin.vue'

	import useHotels from '../composables/useHotels'
	import type { Traveler } from '../interfaces/Traveler'
	import { HotelsSearchRequest } from '../interfaces/hotelsSearchRequest'
	import QuickDestinationEdit from './QuickDestinationEdit.vue'

	interface Props {
		form: HotelsSearchRequest
		isFetching: boolean
		selectedPeople: Array<Traveler>
		reservationNights: number
		setNewRequest: () => void
	}

	const props = defineProps<Props>()

	// Ajuste rápido de estancia (+1/-1 día de checkout) sin abrir el formulario
	// completo: SOLO mueve End (queda "en borrador"). No busca solo — el
	// usuario pica el botón de refrescar para disparar la búsqueda con lo que
	// haya quedado armado (destino y/o fechas). Noches es puro reactive
	// derivado de Start/End, no tiene control propio.
	const { setFormValue, refetch, isFetching: isAdjusting } = useHotels()
	const { setIsQuickSearching } = useHotelsStore()

	const adjustNights = (delta: number) => {
		if (props.reservationNights + delta < 1) return

		const newEnd = moment(String(props.form.End))
			.add(delta, 'days')
			.format('YYYY-MM-DD')
		setFormValue('End', newEnd)
	}

	// Único disparador real de la búsqueda para los ajustes rápidos.
	// Usamos refetch() directo (no searchHotels()): searchHotels() re-valida
	// TODO el form (viajeros, socio, etc. vía hotelVuelitate) antes de buscar,
	// pensado para el submit del form completo. Aquí la búsqueda ya pasó esa
	// validación una vez; solo estamos ajustando parámetros puntuales sobre
	// una búsqueda ya válida, así que revalidar de más solo puede bloquear el
	// ajuste sin motivo (p. ej. si algo no relacionado quedó "sucio").
	const handleRefresh = async () => {
		if (isAdjusting.value || useHotelsStore().isQuickSearching) return
		setIsQuickSearching(true)
		try {
			await refetch()
		} finally {
			setIsQuickSearching(false)
		}
	}
</script>

<template>
	<div class="mx-auto max-w-7xl px-2 sm:px-4 py-2 sm:py-3">
		<div class="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2">
			<!-- Badge base class: bg-white border border-gray-200 -->
			<QuickDestinationEdit>
				<template #value>{{ form.cityName || 'N/A' }}</template>
			</QuickDestinationEdit>

			<div
				class="flex items-center gap-1.5 sm:gap-2 bg-white rounded-lg border border-gray-200 px-2.5 sm:px-3 py-1.5 sm:py-2 shadow-sm"
			>
				<button
					type="button"
					title="Un día menos de estancia"
					:disabled="isAdjusting || reservationNights <= 1"
					class="flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full border border-gray-300 bg-white text-gray-600 hover:border-[#021F58] hover:text-[#021F58] hover:bg-[#021F58]/5 disabled:opacity-30 disabled:hover:border-gray-300 disabled:hover:text-gray-600 disabled:hover:bg-white transition-colors"
					@click="adjustNights(-1)"
				>
					<span class="material-symbols-outlined text-sm sm:text-base"
						>remove</span
					>
				</button>

				<span
					class="material-symbols-outlined text-[#021F58] text-sm sm:text-base"
					>calendar_today</span
				>
				<div class="flex flex-col leading-tight">
					<span
						class="text-[9px] sm:text-[10px] text-gray-400 font-medium uppercase tracking-wide"
						>Estancia</span
					>
					<span
						class="text-[11px] sm:text-sm font-semibold text-gray-900 whitespace-nowrap"
					>
						{{ moment(String(form.Start)).format('DD/MM/YYYY') }} –
						{{ moment(String(form.End)).format('DD/MM/YYYY') }}
					</span>
				</div>

				<button
					type="button"
					title="Un día más de estancia"
					:disabled="isAdjusting"
					class="flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full border border-gray-300 bg-white text-gray-600 hover:border-[#021F58] hover:text-[#021F58] hover:bg-[#021F58]/5 disabled:opacity-30 transition-colors"
					@click="adjustNights(1)"
				>
					<span class="material-symbols-outlined text-sm sm:text-base"
						>add</span
					>
				</button>
			</div>

			<div
				class="flex items-center gap-1.5 sm:gap-2 bg-white rounded-lg border border-gray-200 px-2.5 sm:px-3 py-1.5 sm:py-2 shadow-sm"
			>
				<span
					class="material-symbols-outlined text-[#021F58] text-sm sm:text-base"
					>hotel</span
				>
				<div class="flex flex-col leading-tight">
					<span
						class="text-[9px] sm:text-[10px] text-gray-400 font-medium uppercase tracking-wide"
						>Hab.</span
					>
					<span class="text-[11px] sm:text-sm font-semibold text-gray-900">{{
						form.Quantity
					}}</span>
				</div>
			</div>

			<div
				v-if="selectedPeople.length > 0"
				class="flex items-center gap-1.5 sm:gap-2 bg-white rounded-lg border border-gray-200 px-2.5 sm:px-3 py-1.5 sm:py-2 shadow-sm"
			>
				<span
					class="material-symbols-outlined text-[#021F58] text-sm sm:text-base"
					>group</span
				>
				<div class="flex flex-col leading-tight">
					<span
						class="text-[9px] sm:text-[10px] text-gray-400 font-medium uppercase tracking-wide"
						>Viaj.</span
					>
					<span class="text-[11px] sm:text-sm font-semibold text-gray-900">{{
						selectedPeople.length
					}}</span>
				</div>
			</div>

			<div
				class="flex items-center gap-1.5 sm:gap-2 bg-white rounded-lg border border-gray-200 px-2.5 sm:px-3 py-1.5 sm:py-2 shadow-sm"
			>
				<span
					class="material-symbols-outlined text-[#021F58] text-sm sm:text-base"
					>nights_stay</span
				>
				<div class="flex flex-col leading-tight">
					<span
						class="text-[9px] sm:text-[10px] text-gray-400 font-medium uppercase tracking-wide"
						>Noch.</span
					>
					<span class="text-[11px] sm:text-sm font-semibold text-gray-900">{{
						reservationNights
					}}</span>
				</div>
			</div>

			<button
				type="button"
				title="Actualizar resultados"
				class="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-lg border border-gray-200 bg-white text-[#021F58] shadow-sm hover:bg-gray-100 disabled:opacity-40 transition-colors"
				:disabled="isAdjusting"
				@click="handleRefresh"
			>
				<LoadingSpin
					v-if="isAdjusting"
					class="w-4 h-4"
				/>
				<span
					v-else
					class="material-symbols-outlined text-lg"
					>refresh</span
				>
			</button>

			<button
				class="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-lg text-white shadow-sm transition-colors"
				:class="
					isFetching ?
						'bg-[#021F58]/50 cursor-wait'
					:	'bg-[#021F58] hover:bg-[#1C3F7A]'
				"
				:disabled="isFetching"
				@click="setNewRequest"
			>
				<LoadingSpin
					v-if="isFetching"
					class="w-4 h-4"
				/>
				<span
					v-else
					class="material-symbols-outlined text-lg"
					>keyboard_arrow_up</span
				>
			</button>
		</div>

		<div
			v-if="isAdjusting"
			class="mt-2 flex items-center justify-center gap-2 text-xs font-medium text-[#021F58]"
		>
			<LoadingSpin class="w-3.5 h-3.5" />
			Buscando hoteles...
		</div>
	</div>
</template>
